import React, { useState } from "react";
import { Check, Copy, Loader2 } from "lucide-react";

import { useKbNodeFileUrl } from "@/shared/api/knowledge";
import Markdown from "@/shared/components/markdown/Markdown";
import FilePreview from "@/shared/components/viewer/FilePreview";
import { useObjectUrl } from "./hooks/useObjectUrl";
import { kindOf } from "./preview";

import type { KbNode } from "@/shared/types";

/** Past this many characters a file is cut until the reader asks for the rest. */
export const PREVIEW_CHAR_LIMIT = 200_000;

const note = (text: string) => <p className="m-auto text-[13px] text-[var(--text-tertiary)] text-center px-6">{text}</p>;

/** What the browser may render itself: pdf, and images but not svg (script). Judged by the
 * stored mime on the signed-link path (S3 is told to serve exactly that type) and by the
 * fetched bytes' own type on the blob path — never by the file name. */
const isPdf = (type?: string) => type === "application/pdf";
const isRaster = (type?: string) => !!type?.startsWith("image/") && type !== "image/svg+xml";

/** A pdf or an image, drawn by the browser. */
function Native({ kbId, node }: { kbId: string; node: KbNode }) {
  const stored = node.file?.mime;
  const direct = isPdf(stored) || isRaster(stored);
  // A signed S3 link first; any failure (404 = no bucket or no link) reads the bytes through the API.
  const link = useKbNodeFileUrl(kbId, node.id, direct);
  const viaBlob = !direct || link.isError;
  const blob = useObjectUrl(kbId, node.id, viaBlob);

  const { url, type, error, loading } = viaBlob
    ? blob
    : { url: link.data?.url, type: stored, error: undefined, loading: !link.data };
  if (error) return note(`Could not load the file: ${error}`);
  if (loading || !url) return <Loader2 className="m-auto w-4 h-4 animate-spin text-[var(--text-tertiary)]" />;
  if (isPdf(type)) {
    return <iframe src={url} title={node.name} className="w-full h-full border-0 bg-[var(--surface)]" />;
  }
  if (isRaster(type)) {
    return <img src={url} alt={node.name} className="m-auto max-w-full max-h-full object-contain p-4" />;
  }
  return note("This file cannot be previewed here. Use Download.");
}

/** A sheet or an Office file, drawn from its bytes by the viewer chat uses. */
function Drawn({ kbId, node }: { kbId: string; node: KbNode }) {
  const blob = useObjectUrl(kbId, node.id, true, node.name);
  if (blob.error) return note(`Could not load the file: ${blob.error}`);
  if (blob.loading || !blob.url) return <Loader2 className="m-auto w-4 h-4 animate-spin text-[var(--text-tertiary)]" />;
  return <div className="w-full h-full min-h-0"><FilePreview source={{ name: node.name, file: blob.file }} url={blob.url} /></div>;
}

/** The file as uploaded. Markdown is its own text, rendered. */
export function Original({ kbId, node, text }: { kbId: string; node: KbNode; text: string }) {
  const kind = kindOf(node);
  if (kind === "markdown") {
    return <div className="w-full h-full overflow-y-auto px-6 md:px-10 py-7 text-[14px]"><div className="max-w-[720px] mx-auto"><Markdown text={text.slice(0, PREVIEW_CHAR_LIMIT)} /></div></div>;
  }
  if (kind === "pdf" || kind === "image") return <Native kbId={kbId} node={node} />;
  return <Drawn kbId={kbId} node={node} />;
}

/** The extracted text, line by line, with a copy button. */
export function Raw({ text }: { text: string }) {
  const [full, setFull] = useState(false);
  const [copied, setCopied] = useState(false);
  const cut = !full && text.length > PREVIEW_CHAR_LIMIT;
  const shown = cut ? text.slice(0, PREVIEW_CHAR_LIMIT) : text;
  const lines = shown.split("\n");
  const copy = () =>
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }, () => undefined);

  return (
    <div className="rounded-[18px] bg-[var(--surface-2)] p-1">
      <div className="flex items-center gap-2 min-h-[36px] pl-3 pr-1 pb-1 text-[12px] text-[var(--text-tertiary)]">
        <span className="font-mono tabular-nums">{text.split("\n").length.toLocaleString("en-US")} lines · {[...text].length.toLocaleString("en-US")} characters</span>
        <span className="flex-1" />
        <button onClick={copy} className="press h-7 px-2.5 rounded-[8px] inline-flex items-center gap-1.5 text-[12px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]">
          {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift overflow-hidden">
        <div className="flex font-mono text-[12.5px] leading-[1.65] min-w-0">
          <pre aria-hidden className="m-0 shrink-0 pl-3 pr-3 py-3 text-right select-none text-[var(--text-quaternary)] tabular-nums border-r hairline">
            {lines.map((_, i) => i + 1).join("\n")}
          </pre>
          <pre className="m-0 py-3 px-4 min-w-0 flex-1 overflow-x-auto text-[var(--text-primary)]">{shown}</pre>
        </div>
        {cut && (
          <div className="flex justify-center border-t hairline py-3">
            <button
              onClick={() => setFull(true)}
              className="press px-3 py-1.5 rounded-[8px] text-[13px] ring-1 ring-[var(--hairline-strong)] hover:bg-[var(--accent-hover)]"
            >
              Load full file
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
